// `?start=<goal>` on the buttons that open the app (docs/first-win-onboarding-plan.md,
// lane 10). A page that already says what someone came for (Discover, Store,
// newsletters, …) sends them in with that goal, so the app skips "What do you
// want to do first?" and starts them on it. The app also reads the
// first-touch landing page from the attribution cookie; this covers the page
// they actually clicked from.
//
// One click listener instead of 117 page edits: it adds the goal to any link
// that opens the app's front door (`app.eden.so/`) as it's clicked.
//
// COPY of `LANDING_PATH_GOALS` in `packages/schema/src/firstSteps.ts` (the
// landing build does not import @canvas/schema). `startGoalLinks.test.ts`
// fails when the two drift.

export const LANDING_PATH_GOALS: Readonly<Record<string, string>> = {
  "/features/strategy": "viral",
  "/brand-strategy": "viral",
  "/features/discover": "viral",
  "/features/creators": "viral",
  "/features/youtube": "viral",
  "/features/scheduling": "viral",
  "/vs/1of10": "viral",
  "/vs/viewstats": "viral",
  "/vs/sandcastles": "viral",
  "/vs/spotter": "viral",
  "/vs/hypefury": "viral",
  "/vs/typefully": "viral",
  "/vs/taplio": "viral",
  "/vs/tweethunter": "viral",
  "/save": "save",
  "/features/chrome-extension": "save",
  "/features/highlights": "save",
  "/features/reader": "save",
  "/vs/readwise": "save",
  "/vs/mymind": "save",
  "/vs/raindrop": "save",
  "/features/store": "sell",
  "/features/communities": "sell",
  "/features/courses": "sell",
  "/features/coaching": "sell",
  "/features/digital-downloads": "sell",
  "/features/sell-ai": "sell",
  "/vs/gumroad": "sell",
  "/vs/kajabi": "sell",
  "/vs/stan": "sell",
  "/vs/stanley": "sell",
  "/vs/skool": "sell",
  "/vs/circle": "sell",
  "/vs/mighty-networks": "sell",
  "/vs/patreon": "sell",
  "/vs/whop": "sell",
  "/features/auto-dm": "sell",
  "/vs/manychat": "sell",
  "/features/newsletters": "sell",
  "/features/email-marketing": "sell",
  "/vs/beehiiv": "sell",
  "/vs/kit": "sell",
  "/vs/mailchimp": "sell",
  "/vs/substack": "sell",
  "/teams": "agency",
  "/agencies": "agency",
  "/start/youtube": "viral",
  "/start/research": "viral",
  "/start/selling": "sell",
  "/start/auto-dm": "sell",
  "/start/email": "sell",
};

/** The goal a page path implies, longest prefix first (same rule as the app). */
export function startGoalForPath(pathname: string): string | null {
  const path = (pathname.split(/[?#]/)[0] ?? "").replace(/\/+$/, "").toLowerCase();
  if (!path) return null;
  let best: { prefix: string; goal: string } | null = null;
  for (const [prefix, goal] of Object.entries(LANDING_PATH_GOALS)) {
    if (path !== prefix && !path.startsWith(`${prefix}/`)) continue;
    if (!best || prefix.length > best.prefix.length) best = { prefix, goal };
  }
  return best?.goal ?? null;
}

/**
 * `href` with `?start=<goal>` when it opens the app's front door and has no
 * `start` yet; otherwise null (leave the link alone). Deep links (a Custom AI
 * page, a post) keep their own routing.
 */
export function withStartGoal(href: string, appOrigin: string, goal: string): string | null {
  let url: URL;
  let app: URL;
  try {
    url = new URL(href);
    app = new URL(appOrigin);
  } catch {
    return null;
  }
  if (url.origin !== app.origin) return null;
  if (url.pathname !== "/" && url.pathname !== "") return null;
  if (url.searchParams.has("start")) return null;
  url.searchParams.set("start", goal);
  return url.toString();
}

// The goal a visitor picked on the page itself (the homepage's part
// switcher, components/home-paths). Used when the path implies none.
let pickedGoal: string | null = null;

/** Remember the goal a visitor picked, so later app links carry it. */
export function setPickedStartGoal(goal: string | null): void {
  pickedGoal = goal;
}

/** Install once on the client: rewrites app links as they're clicked. */
export function installStartGoalLinks(appOrigin: string): void {
  if (typeof document === "undefined") return;
  const onClick = (event: MouseEvent) => {
    const target = event.target as Element | null;
    const link = target?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!link) return;
    const goal = startGoalForPath(window.location.pathname) ?? pickedGoal;
    if (!goal) return;
    const next = withStartGoal(link.href, appOrigin, goal);
    if (next) link.href = next;
  };
  // Capture phase, before the browser follows the link; auxclick covers
  // middle-click "open in new tab".
  document.addEventListener("click", onClick, true);
  document.addEventListener("auxclick", onClick, true);
}
